import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Calculator, DollarSign, TrendingUp, Clock, Home, Utensils, Plus, Copy,
  Trophy, ArrowRight, Link2, RefreshCw, AlertCircle, Sparkles, Trash2, Edit2,
} from "lucide-react";

/* ---------------- Defaults & helpers ---------------- */

const BLANK = {
  name: "Offer",
  bill_rate: 125, taxable_rate: 25,
  housing_stipend: 1400, meal_stipend: 350,
  weekly_hours: 36, overtime_rate: 0, assignment_weeks: 13,
};

const STORE_KEY = "tc_travel_offers_v1";

const fmtUSD = (n) => (n ?? 0).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtUSD2 = (n) => (n ?? 0).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });

function encodeOffers(offers) {
  try { return btoa(unescape(encodeURIComponent(JSON.stringify(offers)))).replace(/\+/g,"-").replace(/\//g,"_").replace(/=+$/,""); }
  catch { return ""; }
}
function decodeOffers(b) {
  try {
    const pad = "=".repeat((4 - (b.length % 4)) % 4);
    return JSON.parse(decodeURIComponent(escape(atob((b + pad).replace(/-/g,"+").replace(/_/g,"/")))));
  } catch { return null; }
}

/* ---------------- Page ---------------- */

export default function TravelCalculator() {
  // Initial load: from hash fragment → else localStorage → else one blank offer
  const [offers, setOffers] = useState(() => {
    const hash = (typeof window !== "undefined" ? window.location.hash : "").replace(/^#o=/, "");
    if (hash) {
      const parsed = decodeOffers(hash);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
    try {
      const raw = localStorage.getItem(STORE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch {}
    return [ { ...BLANK, name: "Offer A" } ];
  });
  const [resultsById, setResultsById] = useState({});     // {0: result, 1: result}
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Persist offers locally
  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(offers)); } catch {}
  }, [offers]);

  // Recalculate every offer whenever they change
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const pairs = await Promise.all(offers.map(async (o, idx) => {
          const { name: _name, ...payload } = o;
          const { data } = await api.post("/calculator/travel", payload);
          return [idx, data];
        }));
        if (!cancelled) setResultsById(Object.fromEntries(pairs));
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const h = setTimeout(run, 300);  // debounce
    return () => { cancelled = true; clearTimeout(h); };
  }, [JSON.stringify(offers)]); // eslint-disable-line react-hooks/exhaustive-deps

  const updOffer = (i, patch) => setOffers((prev) => prev.map((o, idx) => idx === i ? { ...o, ...patch } : o));
  const addOffer = () => {
    if (offers.length >= 3) return;
    setOffers((prev) => [...prev, { ...BLANK, name: `Offer ${String.fromCharCode(65 + prev.length)}` }]);
  };
  const removeOffer = (i) => setOffers((prev) => prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i));
  const duplicateOffer = (i) => {
    if (offers.length >= 3) return;
    setOffers((prev) => {
      const src = prev[i];
      return [...prev, { ...src, name: `${src.name} copy` }];
    });
  };
  const resetAll = () => setOffers([{ ...BLANK, name: "Offer A" }]);

  const best = useMemo(() => {
    let bestIdx = -1, bestVal = -Infinity;
    Object.entries(resultsById).forEach(([i, r]) => {
      const v = r?.assignment_total || 0;
      if (v > bestVal) { bestVal = v; bestIdx = Number(i); }
    });
    return { idx: bestIdx, total: bestVal };
  }, [resultsById]);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const hash = encodeOffers(offers);
    return `${window.location.origin}${window.location.pathname}#o=${hash}`;
  }, [offers]);

  const copyShare = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(
      () => toast.success("Shareable link copied", { description: "Recipients see the same offers you're comparing." }),
      () => toast.error("Could not copy link — long-press the link field to copy manually."),
    );
  };

  /* ---------------- Render ---------------- */
  return (
    <div className="space-y-6" data-testid="travel-calculator">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-trital-red">Tools</div>
          <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl text-slate-900 tracking-tight">Travel Assignment Calculator</h1>
          <p className="text-slate-600 text-[14px] max-w-2xl">Transparent math on weekly gross, taxable pay, stipends, blended rate, and 13-week total. Add up to 3 offers to compare side-by-side.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyShare} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 text-[12px] font-semibold hover:bg-slate-50" data-testid="calc-share">
            <Link2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Share compare
          </button>
          <button onClick={resetAll} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 text-slate-500 text-[12px] font-semibold hover:bg-slate-50" data-testid="calc-reset" title="Reset to a single blank offer">
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.8} /> Reset
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 flex gap-2 items-start" data-testid="calc-error">
          <AlertCircle className="w-4 h-4 text-trital-red mt-0.5 shrink-0" strokeWidth={1.8} />
          <div className="text-[12.5px] text-red-800">{error}</div>
        </div>
      )}

      {/* Offers — compare grid */}
      <div className={`grid gap-5 ${offers.length === 1 ? "lg:grid-cols-1" : offers.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3"}`} data-testid="calc-offers">
        {offers.map((o, i) => (
          <OfferCard
            key={i}
            idx={i}
            offer={o}
            result={resultsById[i]}
            isWinner={best.idx === i && offers.length > 1}
            canRemove={offers.length > 1}
            canDuplicate={offers.length < 3}
            onChange={(patch) => updOffer(i, patch)}
            onRemove={() => removeOffer(i)}
            onDuplicate={() => duplicateOffer(i)}
          />
        ))}
        {offers.length < 3 && (
          <button
            onClick={addOffer}
            className="rounded-3xl border-2 border-dashed border-slate-300 bg-white hover:border-trital-navy hover:bg-slate-50 transition-colors p-6 flex flex-col items-center justify-center gap-2 min-h-[420px]"
            data-testid="calc-add-offer"
          >
            <div className="w-12 h-12 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
              <Plus className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <div className="font-display font-bold text-[15px] text-slate-900">Add another offer</div>
            <div className="text-[12px] text-slate-500">Compare up to 3 offers side-by-side</div>
          </button>
        )}
      </div>

      {/* Summary band (compare mode) */}
      {offers.length > 1 && best.idx >= 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-trital-navy-ink text-white p-6 lg:p-8 relative overflow-hidden shadow-navy"
          data-testid="calc-summary"
        >
          <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" />
          <div className="relative grid lg:grid-cols-[1fr_auto] items-center gap-6">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-[10.5px] font-bold uppercase tracking-[0.18em] text-white">
                <Trophy className="w-3 h-3 text-amber-300" strokeWidth={2} /> Best total (13 weeks)
              </div>
              <div className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight">
                {offers[best.idx].name} · <span className="text-white">{fmtUSD(best.total)}</span>
              </div>
              <div className="mt-2 text-[13px] text-white/70">
                Margin over the next best:{" "}
                <span className="font-bold text-white">
                  {fmtUSD(best.total - Math.max(...Object.entries(resultsById).filter(([i]) => Number(i) !== best.idx).map(([, r]) => r?.assignment_total || 0)))}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={copyShare} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-trital-navy text-[13px] font-semibold hover:bg-slate-100" data-testid="calc-summary-share">
                <Copy className="w-4 h-4" strokeWidth={1.8} /> Copy compare link
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Comparison math — always visible for transparency */}
      {offers.length > 1 && (
        <CompareTable offers={offers} results={resultsById} bestIdx={best.idx} />
      )}

      {/* Math transparency */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card" data-testid="calc-transparency">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-trital-red" strokeWidth={1.8} />
          <div className="font-display font-bold text-slate-900">How the math works</div>
        </div>
        <div className="mt-3 grid md:grid-cols-2 gap-3 text-[12.5px] text-slate-600 leading-relaxed">
          <div><span className="font-bold text-slate-900">Weekly gross</span> = (taxable hourly × weekly hours) + housing stipend + meal stipend + overtime (if applicable).</div>
          <div><span className="font-bold text-slate-900">Blended rate</span> = weekly gross ÷ weekly hours — the effective $/hr you can compare apples-to-apples across offers.</div>
          <div><span className="font-bold text-slate-900">Taxable vs stipend</span> — only the taxable portion affects W-2 wages; stipends are non-taxable per IRS rules when tax-home requirements are met.</div>
          <div><span className="font-bold text-slate-900">Overtime</span> applies only to hours beyond 40/week at your quoted OT rate. Leave at 0 if not offered.</div>
        </div>
        <div className="mt-4 text-[11px] text-slate-500">Estimates for planning only. Always confirm stipend eligibility and OT policy with your recruiter for contract-specific numbers.</div>
      </div>

      {/* Loading hint */}
      {loading && <div className="text-[11px] text-slate-400 flex items-center gap-1.5 justify-center" data-testid="calc-loading"><RefreshCw className="w-3 h-3 animate-spin" strokeWidth={1.8} /> Recalculating…</div>}
    </div>
  );
}

/* ---------------- Offer card ---------------- */

function OfferCard({ idx, offer, result, isWinner, canRemove, canDuplicate, onChange, onRemove, onDuplicate }) {
  const [editingName, setEditingName] = useState(false);
  const tid = (k) => `calc-${idx}-${k}`;

  const fields = [
    { k: "bill_rate",         label: "Bill rate ($/hr)",          Icon: DollarSign,   step: 1 },
    { k: "taxable_rate",      label: "Taxable hourly rate",       Icon: DollarSign,   step: 0.5 },
    { k: "housing_stipend",   label: "Housing stipend (weekly)",  Icon: Home,         step: 25 },
    { k: "meal_stipend",      label: "Meal stipend (weekly)",     Icon: Utensils,     step: 25 },
    { k: "weekly_hours",      label: "Weekly hours",              Icon: Clock,        step: 1 },
    { k: "overtime_rate",     label: "Overtime rate (>40h)",      Icon: TrendingUp,   step: 0.5 },
    { k: "assignment_weeks",  label: "Assignment length (wk)",    Icon: Clock,        step: 1 },
  ];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}
      className={`relative rounded-3xl bg-white border p-5 shadow-card transition-colors ${isWinner ? "border-emerald-300 ring-1 ring-emerald-300/60" : "border-slate-200"}`}
      data-testid={`calc-offer-${idx}`}
    >
      {isWinner && (
        <div className="absolute -top-2.5 left-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-[0.14em] shadow" data-testid={`calc-offer-${idx}-winner`}>
          <Trophy className="w-3 h-3" strokeWidth={2} /> Best total
        </div>
      )}

      {/* Name + actions */}
      <div className="flex items-center gap-2">
        {editingName ? (
          <input
            autoFocus
            value={offer.name}
            onChange={(e) => onChange({ name: e.target.value.slice(0, 36) })}
            onBlur={() => setEditingName(false)}
            onKeyDown={(e) => { if (e.key === "Enter") setEditingName(false); }}
            className="font-display font-black text-[18px] text-slate-900 border-b border-slate-300 focus:outline-none focus:border-trital-navy px-1 py-0.5 bg-transparent flex-1"
            data-testid={`calc-offer-${idx}-name-input`}
          />
        ) : (
          <button
            onClick={() => setEditingName(true)}
            className="group inline-flex items-center gap-1.5 font-display font-black text-[18px] text-slate-900 truncate"
            data-testid={`calc-offer-${idx}-name`}
            title="Click to rename"
          >
            {offer.name}
            <Edit2 className="w-3.5 h-3.5 text-slate-300 group-hover:text-trital-navy" strokeWidth={1.8} />
          </button>
        )}
        <div className="ml-auto flex items-center gap-1">
          {canDuplicate && (
            <button onClick={onDuplicate} className="p-1.5 rounded-md text-slate-400 hover:text-trital-navy hover:bg-slate-100" title="Duplicate this offer" data-testid={`calc-offer-${idx}-duplicate`}>
              <Copy className="w-4 h-4" strokeWidth={1.8} />
            </button>
          )}
          {canRemove && (
            <button onClick={onRemove} className="p-1.5 rounded-md text-slate-400 hover:text-trital-red hover:bg-slate-100" title="Remove this offer" data-testid={`calc-offer-${idx}-remove`}>
              <Trash2 className="w-4 h-4" strokeWidth={1.8} />
            </button>
          )}
        </div>
      </div>

      {/* Inputs */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        {fields.map(({ k, label, Icon, step }) => (
          <div key={k}>
            <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1">
              <Icon className="w-3 h-3" strokeWidth={1.8} /> {label}
            </label>
            <input
              type="number"
              step={step}
              min={0}
              value={offer[k]}
              onChange={(e) => onChange({ [k]: parseFloat(e.target.value || 0) })}
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy"
              data-testid={tid(k)}
            />
          </div>
        ))}
      </div>

      {/* Results pane */}
      <div className="mt-5 rounded-2xl bg-trital-navy-ink text-white p-5 relative overflow-hidden">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" />
        <div className="relative">
          <div className="text-[10.5px] uppercase tracking-wider text-white/60 font-bold">Weekly gross</div>
          <div className="mt-1 font-display font-black text-4xl tracking-tight">{fmtUSD(result?.weekly_gross)}</div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-[12px]">
            <Metric label="Blended rate" value={result?.blended_rate != null ? `$${Number(result.blended_rate).toFixed(2)}/hr` : "—"} />
            <Metric label="Annual est."  value={fmtUSD(result?.annual_estimate)} />
            <Metric label="Taxable pay"  value={fmtUSD(result?.taxable_pay)} />
            <Metric label="Stipends"     value={fmtUSD(result?.stipends)} />
            {result?.overtime_pay > 0 && <Metric label="Overtime"  value={fmtUSD(result.overtime_pay)} />}
            <Metric label="Assignment total" value={fmtUSD(result?.assignment_total)} highlight tid={`calc-offer-${idx}-total`} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Metric({ label, value, highlight = false, tid }) {
  return (
    <div className={`rounded-lg p-2.5 ${highlight ? "bg-trital-red/20 border border-trital-red/30" : "bg-white/5 border border-white/10"}`} data-testid={tid}>
      <div className="text-[9.5px] uppercase tracking-wider text-white/60 font-bold">{label}</div>
      <div className="mt-0.5 font-display font-bold text-[14px] text-white">{value}</div>
    </div>
  );
}

/* ---------------- Compare table ---------------- */

function CompareTable({ offers, results, bestIdx }) {
  const rows = [
    { k: "weekly_gross",     l: "Weekly gross",           fmt: fmtUSD },
    { k: "taxable_pay",      l: "Taxable pay",            fmt: fmtUSD },
    { k: "stipends",         l: "Stipends (wk)",          fmt: fmtUSD },
    { k: "overtime_pay",     l: "Overtime (wk)",          fmt: fmtUSD },
    { k: "blended_rate",     l: "Blended rate ($/hr)",    fmt: (n) => n != null ? `$${Number(n).toFixed(2)}` : "—" },
    { k: "annual_estimate",  l: "Annualised est.",        fmt: fmtUSD },
    { k: "assignment_total", l: "Assignment total",       fmt: fmtUSD, highlight: true },
  ];
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden" data-testid="calc-compare-table">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <Calculator className="w-4 h-4 text-trital-navy" strokeWidth={1.8} />
        <div className="font-display font-bold text-slate-900">Compare math · {offers.length} offers</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-5 py-2.5 text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">Metric</th>
              {offers.map((o, i) => (
                <th key={i} className={`text-right px-4 py-2.5 text-[10.5px] uppercase tracking-wider font-bold ${i === bestIdx ? "text-emerald-700" : "text-slate-500"}`}>
                  {o.name}{i === bestIdx ? " · best" : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(({ k, l, fmt, highlight }) => (
              <tr key={k} className={highlight ? "bg-slate-50/70" : ""}>
                <td className={`px-5 py-2.5 text-slate-600 ${highlight ? "font-bold text-slate-900" : ""}`}>{l}</td>
                {offers.map((_, i) => (
                  <td key={i} className={`px-4 py-2.5 text-right tabular-nums ${highlight ? "font-bold text-slate-900" : "text-slate-700"} ${i === bestIdx && k === "assignment_total" ? "text-emerald-700" : ""}`}>
                    {fmt(results[i]?.[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
